<template>
  <div class="value-formatting-section">
    <div class="section-header" @click="open = !open">
      <strong>formatting</strong>
      <span v-if="active" class="format-badge">overrides field</span>
      <span class="section-toggle">{{ open ? '▾' : '▸' }}</span>
    </div>
    <p class="format-hint">replaces the spreadsheet field's formatting on this view. leave unset to keep that formatting.</p>
    <p v-if="inheritedPreview && !active" class="format-hint">this field currently shows {{ inheritedPreview }}</p>

    <div v-if="open" class="format-body">
      <template v-if="formatKind === 'number'">
        <b-row class="mb-2">
          <b-col cols="6">
            <label class="form-label">decimal places</label>
            <b-form-input v-model.number="vfDecimalPlaces" type="number" min="0" max="10" size="sm" placeholder="auto" />
          </b-col>
          <b-col cols="6">
            <label class="form-label">currency</label>
            <b-form-select v-model="vfCurrency" :options="currencyOptions" size="sm" />
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col cols="6">
            <label class="form-label">thousands separator</label>
            <b-form-select v-model="vfThousandsSeparator" :options="thousandsSeparatorOptions" size="sm" />
          </b-col>
          <b-col cols="6">
            <label class="form-label">decimal separator</label>
            <b-form-select v-model="vfDecimalSeparator" :options="decimalSeparatorOptions" size="sm" />
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col cols="6">
            <label class="form-label">rounding</label>
            <b-form-select v-model="vfRounding" :options="roundingOptions" size="sm" />
          </b-col>
          <b-col cols="6">
            <b-form-checkbox v-model="vfPercentMode" class="mt-4">display as percentage</b-form-checkbox>
          </b-col>
        </b-row>
        <p v-if="active && numberPreview" class="format-hint">this view shows {{ numberPreview }}</p>
      </template>

      <template v-if="formatKind === 'text'">
        <div class="format-group">
          <div class="format-group-title">words</div>
          <p v-if="dateText" class="format-hint">applied to the date shown in this view.</p>
          <b-row class="mb-2">
            <b-col cols="6">
              <label class="form-label">max display length</label>
              <b-form-input v-model.number="vfMaxLength" type="number" min="1" size="sm" placeholder="unlimited" />
            </b-col>
            <b-col cols="6">
              <label class="form-label">case</label>
              <b-form-select v-model="vfCaseTransform" :options="caseTransformOptions" size="sm" />
            </b-col>
          </b-row>
          <div class="check-row">
            <b-form-checkbox v-model="vfStripHtml">strip HTML</b-form-checkbox>
            <b-form-checkbox v-model="vfStripNonAlphanumeric">strip non-alphanumeric</b-form-checkbox>
            <b-form-checkbox v-model="vfTrimWhitespace">trim whitespace</b-form-checkbox>
          </div>
        </div>

        <div class="format-group">
          <div class="format-group-title">appearance</div>
          <b-row class="mb-2">
            <b-col cols="6">
              <label class="form-label">font</label>
              <b-form-select v-model="vfFontFamily" :options="fontFamilyOptions" size="sm" />
            </b-col>
            <b-col cols="6">
              <label class="form-label">font size</label>
              <b-form-input v-model.number="vfFontSize" type="number" min="8" max="96" size="sm" placeholder="default" />
            </b-col>
          </b-row>
          <div class="check-row">
            <b-form-checkbox v-model="vfBold">bold</b-form-checkbox>
            <b-form-checkbox v-model="vfItalic">italic</b-form-checkbox>
            <b-form-checkbox v-model="vfStrike">strikethrough</b-form-checkbox>
          </div>
        </div>

        <div class="format-group">
          <div class="format-group-title">replacements</div>
          <p class="format-hint">swap a phrase in the displayed text. the stored value stays the same.</p>
          <div v-if="vfReplacements.length" class="replacement-list">
            <div class="replacement-head">
              <span>find</span>
              <span></span>
              <span>show as</span>
              <span></span>
            </div>
            <div v-for="(row, index) in vfReplacements" :key="index" class="replacement-row">
              <b-form-input v-model="row.find" size="sm" placeholder="hello" aria-label="find" />
              <span class="replacement-arrow" aria-hidden="true">→</span>
              <b-form-input v-model="row.replace" size="sm" placeholder="hi" aria-label="show as" />
              <button type="button" class="replacement-remove" aria-label="remove replacement" @click="removeReplacement(index)">×</button>
            </div>
          </div>
          <button v-if="vfReplacements.length < 20" type="button" class="text-action" @click="addReplacement">add a replacement</button>
        </div>
      </template>

      <button v-if="active" type="button" class="text-action format-clear" @click="clear">use field formatting</button>
    </div>
  </div>
</template>

<script>
import { CURRENCIES, FONT_FAMILIES, formatFieldDisplay } from '../../../../shared/value-format.js'

function finiteNumber (value) {

  if (value === null || value === undefined || value === '') return null

  const number = Number(value)

  return Number.isFinite(number) ? number : null
}

export default {
  props: {
    formatKind: { type: String, default: '' },
    seed: { type: Object, default: null },
    inherited: { type: Object, default: null },
    dateText: { type: Boolean, default: false }
  },
  data () {
    return {
      open: false,
      vfDecimalPlaces: null,
      vfThousandsSeparator: null,
      vfDecimalSeparator: '.',
      vfCurrency: null,
      vfPercentMode: false,
      vfRounding: null,
      vfMaxLength: null,
      vfStripHtml: false,
      vfStripNonAlphanumeric: false,
      vfTrimWhitespace: false,
      vfCaseTransform: null,
      vfReplacements: [],
      vfBold: false,
      vfItalic: false,
      vfStrike: false,
      vfFontFamily: null,
      vfFontSize: null,
      currencyOptions: [
        { value: null, text: 'none' },
        ...Object.keys(CURRENCIES).map((key) => ({ value: key, text: `${key} (${CURRENCIES[key].symbol})` }))
      ],
      thousandsSeparatorOptions: [
        { value: null, text: 'none' },
        { value: ',', text: 'comma (1,000)' },
        { value: '.', text: 'period (1.000)' },
        { value: ' ', text: 'space (1 000)' }
      ],
      decimalSeparatorOptions: [
        { value: '.', text: 'period (0.50)' },
        { value: ',', text: 'comma (0,50)' }
      ],
      roundingOptions: [
        { value: null, text: 'round' },
        { value: 'floor', text: 'round down' },
        { value: 'ceil', text: 'round up' }
      ],
      caseTransformOptions: [
        { value: null, text: 'none' },
        { value: 'uppercase', text: 'UPPERCASE' },
        { value: 'lowercase', text: 'lowercase' },
        { value: 'capitalize', text: 'Capitalize Words' }
      ],
      fontFamilyOptions: [
        { value: null, text: 'default' },
        ...FONT_FAMILIES.map((family) => ({ value: family, text: family.split(',')[0].replace(/"/g, '') }))
      ]
    }
  },
  computed: {
    active () {
      return !!this.output()
    },
    numberPreview () {
      if (this.formatKind !== 'number' || !this.active) return ''
      const display = formatFieldDisplay(this.vfPercentMode ? 0.4567 : 1234567.89, this.output())
      return display ? display.text : ''
    },
    inheritedPreview () {
      if (!this.inherited || this.inherited.type !== this.formatKind) return ''
      const sample = this.formatKind === 'number'
        ? (this.inherited.percentMode ? 0.4567 : 1234567.89)
        : 'Example text'
      const display = formatFieldDisplay(sample, this.inherited)
      return display ? display.text : ''
    },
    formattingSnapshot () {
      return JSON.stringify(this.output())
    }
  },
  watch: {
    seed: {
      immediate: true,
      handler (value) {
        this.load(value)
      }
    },
    formatKind () {
      this.load(this.seed)
    },
    formattingSnapshot (value) {
      this.$emit('formatting-change', value === 'null' ? null : JSON.parse(value))
    }
  },
  methods: {
    load (formatting) {
      this.vfDecimalPlaces = null
      this.vfThousandsSeparator = null
      this.vfDecimalSeparator = '.'
      this.vfCurrency = null
      this.vfPercentMode = false
      this.vfRounding = null
      this.vfMaxLength = null
      this.vfStripHtml = false
      this.vfStripNonAlphanumeric = false
      this.vfTrimWhitespace = false
      this.vfCaseTransform = null
      this.vfReplacements = []
      this.vfBold = false
      this.vfItalic = false
      this.vfStrike = false
      this.vfFontFamily = null
      this.vfFontSize = null

      if (!formatting || !formatting.type || formatting.type !== this.formatKind) {
        this.open = false
        return
      }

      this.open = true

      if (formatting.type === 'number') {
        this.vfDecimalPlaces = formatting.decimalPlaces !== undefined && formatting.decimalPlaces !== null
          ? formatting.decimalPlaces
          : null
        this.vfThousandsSeparator = formatting.thousandsSeparator || null
        this.vfDecimalSeparator = formatting.decimalSeparator || '.'
        this.vfCurrency = formatting.currency || null
        this.vfPercentMode = !!formatting.percentMode
        this.vfRounding = formatting.rounding === 'floor' || formatting.rounding === 'ceil' ? formatting.rounding : null
        return
      }

      this.vfMaxLength = formatting.maxLength || null
      this.vfStripHtml = !!formatting.stripHtml
      this.vfStripNonAlphanumeric = !!formatting.stripNonAlphanumeric
      this.vfTrimWhitespace = !!formatting.trimWhitespace
      this.vfCaseTransform = formatting.caseTransform || null
      this.vfReplacements = Array.isArray(formatting.replacements)
        ? formatting.replacements.map((row) => ({ find: row.find || '', replace: row.replace || '' }))
        : []
      this.vfBold = !!formatting.bold
      this.vfItalic = !!formatting.italic
      this.vfStrike = !!formatting.strike
      this.vfFontFamily = formatting.fontFamily || null
      this.vfFontSize = formatting.fontSize || null
    },
    clear () {
      this.load(null)
    },
    addReplacement () {
      if (this.vfReplacements.length >= 20) return
      this.vfReplacements.push({ find: '', replace: '' })
    },
    removeReplacement (index) {
      this.vfReplacements.splice(index, 1)
    },
    output () {
      if (this.formatKind === 'number') {
        const decimalPlaces = finiteNumber(this.vfDecimalPlaces)
        const places = decimalPlaces === null ? null : Math.max(0, Math.min(10, decimalPlaces))
        const untouched = places === null && !this.vfThousandsSeparator &&
          (this.vfDecimalSeparator || '.') === '.' && !this.vfCurrency && !this.vfPercentMode &&
          this.vfRounding !== 'floor' && this.vfRounding !== 'ceil'
        if (untouched) return null
        return {
          type: 'number',
          decimalPlaces: places,
          rounding: this.vfRounding === 'floor' || this.vfRounding === 'ceil' ? this.vfRounding : null,
          thousandsSeparator: this.vfThousandsSeparator || null,
          decimalSeparator: this.vfDecimalSeparator || '.',
          currency: this.vfCurrency || null,
          percentMode: !!this.vfPercentMode
        }
      }

      if (this.formatKind === 'text') {
        const replacements = (this.vfReplacements || []).filter((row) => row && row.find).slice(0, 20)
        const maxLength = finiteNumber(this.vfMaxLength)
        const fontSize = finiteNumber(this.vfFontSize)
        const untouched = !maxLength && !this.vfStripHtml && !this.vfStripNonAlphanumeric &&
          !this.vfTrimWhitespace && !this.vfCaseTransform && !replacements.length &&
          !this.vfBold && !this.vfItalic && !this.vfStrike && !this.vfFontFamily && !fontSize
        if (untouched) return null
        return {
          type: 'text',
          maxLength: maxLength && maxLength > 0 ? maxLength : null,
          stripHtml: !!this.vfStripHtml,
          stripNonAlphanumeric: !!this.vfStripNonAlphanumeric,
          trimWhitespace: !!this.vfTrimWhitespace,
          caseTransform: this.vfCaseTransform || null,
          replacements,
          bold: !!this.vfBold,
          italic: !!this.vfItalic,
          strike: !!this.vfStrike,
          fontFamily: this.vfFontFamily || null,
          fontSize: fontSize && fontSize >= 8 ? Math.min(96, fontSize) : null
        }
      }

      return null
    }
  }
}
</script>

<style scoped>
.value-formatting-section {
  border: 1px solid #2a2a28;
  border-radius: 6px;
  padding: 12px;
  margin: 8px 0 16px;
  background: #161614;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  color: #d0d0ce;
  font-size: 0.9em;
}
.section-toggle {
  margin-left: auto;
  color: #7a7a78;
}
.format-badge {
  font-size: 0.72em;
  font-weight: 600;
  color: #141412;
  background: #9ad12b;
  border-radius: 8px;
  padding: 1px 6px;
}
.format-hint {
  color: #888;
  font-size: 0.78em;
  font-style: italic;
  margin: 8px 0 0;
}
.format-body {
  margin-top: 12px;
}
.format-body :deep(.form-label) {
  display: block;
  color: #8a8a88;
  font-size: 0.85rem;
}
.format-group {
  margin-top: 16px;
}
.format-group-title {
  color: #9ad12b;
  font-size: 0.72em;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.check-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
}
.replacement-list {
  margin-top: 10px;
}
.replacement-head,
.replacement-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px minmax(0, 1fr) 28px;
  gap: 8px;
  align-items: center;
}
.replacement-head {
  margin-bottom: 4px;
  color: #6a6a67;
  font-size: 0.75em;
}
.replacement-row {
  margin-bottom: 6px;
}
.replacement-arrow {
  color: #6a6a67;
  text-align: center;
}
.replacement-remove {
  width: 28px;
  height: 28px;
  padding: 0;
  color: #8a8a88;
  background: transparent;
  border: 0;
  font-size: 1.1em;
  line-height: 1;
  cursor: pointer;
}
.replacement-remove:hover {
  color: #f82471;
}
.text-action {
  display: inline-block;
  margin-top: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: #9ad12b;
  font-size: 0.82em;
  cursor: pointer;
}
.text-action:hover {
  text-decoration: underline;
}
.format-clear {
  margin-top: 14px;
}
</style>
